import React, { useEffect, useState } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { CACHE_QUERY_TYPE } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import FLFQueryTypeControlPanel from "../../components/ControlPanel/FLFQueryTypeControlPanel";
import {
  createFLFQueryType,
  getFLFQueryType,
  removeFLFQueryType,
  updateFLFQueryType,
} from "src/apis/FLF/queryType.api";
import QueryTypeTable from "../../components/Table/QueryTypeTable";

const initQueryTypeData = {
  list: [],
  total_record: 0,
};

const FLFQueryType = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [queryTypeData, setQueryTypeData] = useState(initQueryTypeData);
  const [filterQueryTypes, setFilterQueryTypes] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchQueryType = useCallback(() => {
    getFLFQueryType({}, (result) => {
      setQueryTypeData({ ...result });
      setFilterQueryTypes([...result.list]);
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchQueryType();
    }

    return () => {
      // cancel the request before component unmounts
      isCancelled = true;
    };
  }, [fetchQueryType]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredQueryType = useCallback((queryTypes) => {
    setFilterQueryTypes(queryTypes);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (queryTypes, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return queryTypes.filter((queryType) =>
        queryType.name.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredQueryType = value
        ? searchByKeyWord(queryTypeData.list, value)
        : queryTypeData.list;
      updateFilteredQueryType(filteredQueryType);
    },
    [queryTypeData.list, searchByKeyWord, updateFilteredQueryType]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (queryType, resetForm) => {
      if (queryType.id) {
        updateFLFQueryType(queryType.id, queryType, handleApiResponse);
      } else {
        createFLFQueryType(queryType, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchQueryType();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_QUERY_TYPE);
        }
      }
    },
    [fetchQueryType, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleDelete = (queryTypeDelete) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFQueryType(
          queryTypeDelete.id,
          setCurrentActionCache(CACHE_QUERY_TYPE)
        );
        setQueryTypeData((prev) => {
          return {
            ...prev,
            list: prev.list.filter(
              (queryType) => queryType.id !== queryTypeDelete.id
            ),
            total_record: prev.total_record - 1,
          };
        });

        updateFilteredQueryType((prev) =>
          prev.filter((queryType) => queryType.id !== queryTypeDelete.id)
        );
      }
    });
  };

  const resultFilter = useMemo(
    () => searchByKeyWord(filterQueryTypes, searchKey),
    [filterQueryTypes, searchByKeyWord, searchKey]
  );
  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Query Type</h2>
      </div>

      {/** control pan */}
      <FLFQueryTypeControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFQueryTypeControlPanel>

      {/** table */}
      <QueryTypeTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
      />

      {/** Pagination */}
      <Pagination
        pathname={path.flfQueryType}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFQueryType;
